import * as React from 'react'
import { Remove as ExpandLessIcon, Add as ExpandMoreIcon } from '@mui/icons-material'
import { Alert, Autocomplete, Box, Button, Card, CardContent, CardHeader, Collapse, Container, Divider, Grid2 as Grid, IconButton, MenuItem, TextField, Typography, Table, TableBody, TableRow, TableCell } from '@mui/material'
import TitleBar from 'components/TitleBar'
import useFetchWithAuth from 'hooks/useFetchWithAuth'
import { ErrorContext } from 'contexts/errorContext'
import { tableCellClasses } from '@mui/material/TableCell'
import { styled } from '@mui/system'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { format, parseISO } from 'date-fns'
import { formatCurrency } from 'utils/formatters'
import { money } from 'utils/money'
import { generateHexId } from 'utils/randomHex'

const SummaryRow = styled(TableRow)(({ theme }) => ({
  '&:last-of-type td, &:last-of-type th': {
    border: 0,
  },
}))

const SummaryCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    ...theme.typography.body1,
    paddingLeft: 0,
    paddingRight: 0,
  },
  [`&:last-of-type`]: {
    textAlign: 'right',
    fontWeight: 500,
    //width: '60px',
  },
}))

/**
 * ItemSelect component for displaying item select autocomplete
 */
const ItemSelect = ({ products, addItem, formError }) => {
  const [selectedItem, setSelectedItem] = React.useState(null)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (formError != error) setError(formError)
  }, [formError])

  const handleItemAdd = (e) => {
    if (selectedItem) {
      addItem(selectedItem)
      setSelectedItem(null)
      setError('')
    } else {
      setError('Select the item first')
    }
  }

  return (
    <Card variant="outlined" sx={{ my: 6 }}>
      <CardContent>
        <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
          <Grid size={12}>
            <Autocomplete fullWidth
              value={selectedItem}
              options={
                products.map((product) => ({
                  label: product.title,
                  product
                }))
              }
              onChange={(e, item) => setSelectedItem(item)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item"
                  placeholder="Select item"
                  variant="outlined"
                  error={!!error}
                  helperText={error}
                />
              )}
            />
          </Grid>
        </Grid>
        <Button variant="contained" color="primary" type="button" onClick={handleItemAdd}>
          Add Item
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * OrderItem component for displaying selected item
 */
const OrderItem = ({ item, updateItem, deleteItem }) => {
  const [expanded, setExpanded] = React.useState(false)

  // Handles form changes
  const handleChange = (e) => {
    updateItem(item._id, { [e.target.name]: e.target.value })
  }

  // Select addon
  const selectAddon = (name, option) => {
    if (!option) return

    updateItem(item._id, { addons: [{ name, option: option.value }]})
  }

  // Get selected addon
  const getSelectedAddon = (addon, options) => {
    const selected = item.addons.find(a => a.name === addon.name)?.option
    return options.find(o => o.value === selected) || null
  }

  return (
    <Card variant="outlined" sx={{ my: 3 }}>
      <CardHeader
        onClick={(e) => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
        avatar={
          <IconButton size="small" edge="start" disableRipple={true} sx={{ padding: 0, color: 'text.primary' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        title={
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ mr: 1, color: 'text.primary' }}>{item.product.title}</Typography>
            <Typography sx={{ color: 'text.primary', fontWeight: 'fontWeightMedium', }}>{formatCurrency(item.total, { fromCents: true })}</Typography>
          </Box>
        }
      />
      <Collapse in={expanded}>
        <CardContent>
          {item.product.addons.map((addon) => {
            const options = addon.options.map(option => ({
              label: option.value + (option.price > 0 ? ` (${formatCurrency(option.price, { fromCents: true })})` : ''),
              value: option.value
            }))

            return (
              <Grid container spacing={3} sx={{ mb: 3 }} key={addon.name}>
                <Grid size={12}>
                  <Autocomplete
                    fullWidth
                    options={options}
                    value={getSelectedAddon(addon, options)}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(e, option) => selectAddon(addon.name, option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={addon.name}
                        placeholder="Select item"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )
          })}

          {item.product.addons.length > 0 && (
            <Divider sx={{ mt: 1, mb: 3 }} />
          )}
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={12}>
              <TextField fullWidth variant="outlined" type="number" label="Quantity" name="quantity" value={item.quantity} onChange={handleChange} slotProps={{ htmlInput: { min: 1 } }} />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={12}>
              <TextField fullWidth multiline rows={2} variant="outlined" label="Notes" name="notes" value={item.notes} onChange={handleChange} />
            </Grid>
          </Grid>

          <Button variant="contained" color="error" type="button" onClick={() => deleteItem(item._id)}>
            Delete
          </Button>
        </CardContent>
      </Collapse>
    </Card>
  )
}

/**
 * OrderForm component for displaying order form
 */
const OrderForm = ({ title, formData, setFormData, formErrors, submitForm }) => {
  const [alert, setAlert] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [products, setProducts] = React.useState([])
  const { showError } = React.useContext(ErrorContext)
  const fetchWithAuth = useFetchWithAuth()

  // Fetches all products
  React.useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetchWithAuth('/api/products')
        const data = await response.json()
        setProducts([
          data.find(p => p.title === 'Custom Meal'),
          ...data.filter(p => p.shopifyProductId !== 8317128081653)
        ])
      } catch (error) {
        showError('Failed to fetch products. Please try again or refresh the page.')
        console.error(error.message)
      }
    }

    fetchProducts()
  }, [])

  // Handles form changes
  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => {
      const keys = name.split('.')
      const lastKey = keys.pop()

      // shallow copy top-level state
      const next = { ...prev }
      let current = next

      keys.forEach((key) => {
        current[key] = current[key] ? { ...current[key] } : {}
        current = current[key]
      })

      // set the final value
      current[lastKey] = value

      return next
    })
  }

  // Add item
  const handleItemAdd = (item) => {
    if (!item) return

    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { 
          _id: generateHexId(),
          shopifyProductId: item.product.shopifyProductId,
          product: item.product,
          quantity: 1,
          addons: item.product.addons.map((addon) => ({
            name: addon.name,
            option: addon.options.find(o => o.price === 0).value
          })),
          total: item.product.price,
          notes: ''
        },
      ],
    }))
  }

  const handleItemUpdate = (id, updates) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item._id !== id) return item

        // merge addons
        let addons = item.addons
        
        if (Array.isArray(updates.addons)) { 
          const addonsMap = new Map(addons.map(a => [a.name, a]))

          updates.addons.forEach((addon) => {
            addonsMap.set(addon.name, addon)
          })

          addons = Array.from(addonsMap.values())
        }

        // calculate addons total
        const addonsTotal = addons.reduce((sum, addon) => {
          const price = item.product.addons?.find(a => a.name === addon.name)?.options?.find(o => o.value === addon.option)?.price ?? 0
          return sum + price
        }, 0)

        // calculate item total
        const quantity = updates.quantity !== undefined && parseInt(updates.quantity) > 0 ? updates.quantity : item.quantity
        const total = (item.product.price + addonsTotal) * quantity

        return {
          ...item, ...updates, quantity, addons, total
        }
      }),
    }))
  }

  // Delete item
  const handleItemDelete = (id) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item._id !== id),
    }))
  }

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setAlert(null)
    setIsLoading(true)

    try {
      const response = await submitForm(formData)
      setAlert({ type: response.status, message: response.message })
    } catch (error) {
      showError('Failed to save order. Please try again or refresh the page.')
      console.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Allowed days for pickup/delivery
  const isAllowedDay = (date) => {
    const day = date.getDay()
    // 1 = Monday, 4 = Thursday
    return day !== 1 && day !== 4
  }

  // Calculating order summary
  const orderSummary = React.useMemo(() => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + item.total, 0)
    const discount = formData.discountPct != '' ? itemsTotal * (parseFloat(formData.discountPct) / 100) : 0
    const subTotal = itemsTotal - discount
    const deliveryCharges = money.toCents(parseFloat(formData.deliveryCharges) || 0)

    const netTotal =  subTotal + deliveryCharges
    
    const gst = Math.round((5 * netTotal) / 100)
    const qst = Math.round((9.975 * netTotal) / 100)
    
    const total = netTotal + gst + qst

    return { itemsTotal, discount, subTotal, deliveryCharges, gst, qst, total }
  }, [formData.items, formData.discountPct, formData.deliveryCharges])

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TitleBar title={title} actions={[
        <Button variant="contained" type="submit" disabled={isLoading}>Save</Button>
      ]} />
      <Container maxWidth="xl">
        {alert && (
          <Alert severity={alert.type} sx={{ mt: 6, border: 1, borderColor: alert.type + '.light' }}>
            {alert.message}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid size={{ sm: 12, md: 5 }}>
            <Card variant="outlined" sx={{ my: 6 }}>
              <CardContent>
                
                {/* ---------------- CUSTOMER INFORMATION ---------------- */}
                <Typography variant="body2" component="h6" sx={{ fontWeight: 'fontWeightMedium', textTransform: 'uppercase', mt: 1, mb: 1 }}>
                  Customer Information
                </Typography>

                <Divider sx={{ mt: 1, mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid size={6}>
                    <TextField fullWidth variant="outlined" label="First Name" name="customer.firstName"
                      value={formData.customer.firstName} onChange={handleChange}
                      error={!!formErrors.customer?.firstName} helperText={formErrors.customer?.firstName}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField fullWidth variant="outlined" label="Last Name" name="customer.lastName"
                      value={formData.customer.lastName} onChange={handleChange}
                      error={!!formErrors.customer?.lastName} helperText={formErrors.customer?.lastName}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField fullWidth variant="outlined" type="email" label="Email" name="customer.email"
                      value={formData.customer.email} onChange={handleChange}
                      error={!!formErrors.customer?.email} helperText={formErrors.customer?.email}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField fullWidth variant="outlined" type="tel" label="Phone Number" name="customer.phone"
                      value={formData.customer.phone} onChange={handleChange}
                      error={!!formErrors.customer?.phone} helperText={formErrors.customer?.phone}
                    />
                  </Grid>
                </Grid>

                {/* ---------------- ORDER DETAILS ---------------- */}
                <Typography variant="body2" component="h6" sx={{ fontWeight: 'fontWeightMedium', textTransform: 'uppercase', mt: 5, mb: 1 }}>
                  Order Details
                </Typography>

                <Divider sx={{ mt: 1, mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField select fullWidth variant="outlined" label="Order Type" name="type"
                      value={formData.type} onChange={handleChange}
                      error={!!formErrors.type} helperText={formErrors.type}
                    >
                      <MenuItem value="pickup">Pickup</MenuItem>
                      <MenuItem value="delivery">Delivery</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Delivery Date"
                        format="MM/dd/yyyy"
                        shouldDisableDate={isAllowedDay}
                        value={formData.deliveryDate ? parseISO(formData.deliveryDate) : null}
                        onChange={(newValue) =>
                          handleChange({
                            target: {
                              name: "deliveryDate",
                              value: newValue ? format(newValue, 'yyyy-MM-dd') : "",
                            },
                          })
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            error: !!formErrors.deliveryDate,
                            helperText: formErrors.deliveryDate,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField fullWidth variant="outlined" type="number" label="Discount"
                      name="discountPct" value={formData.discountPct}
                      onChange={handleChange} error={!!formErrors.discountPct}
                      helperText={formErrors.discountPct}
                    />
                  </Grid>
                </Grid>

                {formData.type === "delivery" && (
                  <Grid container spacing={3} sx={{ mt: 3 }}>
                    <Grid size={12}>
                      <TextField fullWidth variant="outlined" type="number" label="Delivery Charges"
                        name="deliveryCharges" value={formData.deliveryCharges}
                        onChange={handleChange} error={!!formErrors.deliveryCharges}
                        helperText={formErrors.deliveryCharges}
                      />
                    </Grid>
                  </Grid>
                )}

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField select fullWidth variant="outlined" label="Is Paid?" name="isPaid"
                      value={formData.isPaid} onChange={handleChange}
                      error={!!formErrors.isPaid} helperText={formErrors.isPaid}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* ---------------- DELIVERY ADDRESS ---------------- */}
                {/* Hide on pickup orders */}
                {formData.type === "delivery" && (
                  <>
                    <Typography variant="body2" component="h6" sx={{ fontWeight: 'fontWeightMedium', textTransform: 'uppercase', mt: 5, mb: 1 }}>
                      Delivery Address
                    </Typography>

                    <Divider sx={{ mt: 1, mb: 3 }} />

                    <Grid container spacing={3}>
                      <Grid size={12}>
                        <TextField fullWidth variant="outlined" label="Address Line 1"
                          name="deliveryAddress.address1" value={formData.deliveryAddress.address1}
                          onChange={handleChange} error={!!formErrors.deliveryAddress?.address1}
                          helperText={formErrors.deliveryAddress?.address1}
                        />
                      </Grid>

                      <Grid size={12}>
                        <TextField fullWidth variant="outlined" label="Address Line 2"
                          name="deliveryAddress.address2" value={formData.deliveryAddress.address2}
                          onChange={handleChange} error={!!formErrors.deliveryAddress?.address2}
                          helperText={formErrors.deliveryAddress?.address2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mt: 3 }}>
                      <Grid size={4}>
                        <TextField fullWidth variant="outlined" label="City"
                          name="deliveryAddress.city" value={formData.deliveryAddress.city}
                          onChange={handleChange} error={!!formErrors.deliveryAddress?.city}
                          helperText={formErrors.deliveryAddress?.city}
                        />
                      </Grid>

                      <Grid size={4}>
                        <TextField select fullWidth variant="outlined" label="Province" name="deliveryAddress.province"
                          value={formData.deliveryAddress.province} onChange={handleChange}
                          error={!!formErrors.deliveryAddress?.province} helperText={formErrors.deliveryAddress?.province}
                        >
                          <MenuItem value="AB">AB</MenuItem>
                          <MenuItem value="BC">BC</MenuItem>
                          <MenuItem value="MB">MB</MenuItem>
                          <MenuItem value="NB">NB</MenuItem>
                          <MenuItem value="NL">NL</MenuItem>
                          <MenuItem value="NS">NS</MenuItem>
                          <MenuItem value="ON">ON</MenuItem>
                          <MenuItem value="PE">PE</MenuItem>
                          <MenuItem value="QC">QC</MenuItem>
                          <MenuItem value="SK">SK</MenuItem>
                          <MenuItem value="NT">NT</MenuItem>
                          <MenuItem value="NU">NU</MenuItem>
                          <MenuItem value="YT">YT</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid size={4}>
                        <TextField fullWidth variant="outlined" label="Postal Code"
                          name="deliveryAddress.zip" value={formData.deliveryAddress.zip}
                          onChange={handleChange} error={!!formErrors.deliveryAddress?.zip}
                          helperText={formErrors.deliveryAddress?.zip}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ sm: 12, md: 4 }}>
            {/* ---------------- ORDER ITEMS ---------------- */}
            {formData.items && (
              <Box sx={{ my: 6 }}>
                {formData.items.map((item) => (
                  <OrderItem key={item._id} item={item} updateItem={handleItemUpdate} deleteItem={handleItemDelete} />
                ))}
              </Box>
            )}
            
            <ItemSelect products={products} addItem={handleItemAdd} formError={formErrors.items ?? ''} />
          </Grid>

          <Grid size={{ sm: 12, md: 3 }}>
            {/* ---------------- ORDER SUMMARY ---------------- */}
            <Card variant="outlined" sx={{ my: 6 }}>
              <CardContent>
                <Table size="small">
                  <TableBody>
                    <SummaryRow>
                      <SummaryCell>Items total</SummaryCell>
                      <SummaryCell>{formatCurrency(orderSummary.itemsTotal, { fromCents: true })}</SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>Discount</SummaryCell>
                      <SummaryCell>
                        {
                          orderSummary.discount > 0 ?
                            <Typography color="error">
                              -{formatCurrency(orderSummary.discount, { fromCents: true })}
                            </Typography>
                          : formatCurrency(0)
                        }
                      </SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>Subtotal</SummaryCell>
                      <SummaryCell>{formatCurrency(orderSummary.subTotal, { fromCents: true })}</SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>Delivery charges</SummaryCell>
                      <SummaryCell>{formatCurrency(orderSummary.deliveryCharges, { fromCents: true })}</SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>GST (5%)</SummaryCell>
                      <SummaryCell>{formatCurrency(orderSummary.gst, { fromCents: true })}</SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>QST (9.975%)</SummaryCell>
                      <SummaryCell>{formatCurrency(orderSummary.qst, { fromCents: true })}</SummaryCell>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryCell>Total</SummaryCell>
                      <SummaryCell>
                        <Typography variant="h5" component="h5" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatCurrency(orderSummary.total, { fromCents: true })}
                        </Typography>
                      </SummaryCell>
                    </SummaryRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default OrderForm